import { Link } from 'react-router-dom';
import { useState } from 'react';
import { toast } from 'sonner';
import { PhoneCall, EnvelopeSimple, MapPin, ArrowUpRight } from '@phosphor-icons/react';
import { SERVICES, CONTACT, SERVICE_AREAS } from '@/data/site';
import { public_api } from '@/lib/api';
import { useContent } from '@/context/ContentContext';

export default function Footer() {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const c = useContent();
  const phoneDisplay = c('contact.phone', CONTACT.phone_display);
  const contactEmail = c('contact.email', CONTACT.email);
  const hours = c('contact.hours', CONTACT.hours);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      await public_api.newsletter(email);
      toast.success("You're on the list — thanks for joining.");
      setEmail('');
    } catch {
      toast.error('Could not subscribe. Please try again.');
    } finally { setBusy(false); }
  };

  return (
    <footer className="bg-ink text-white pt-24 pb-10 mt-24" data-testid="site-footer">
      <div className="container-x">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 pb-16 border-b border-white/10">
          {/* Brand & Newsletter */}
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl bg-white flex items-center justify-center p-1">
                <img src="/logo-mark.png" alt="Inclusive Mobility Group" className="w-full h-full object-contain" />
              </div>
              <div className="font-display font-extrabold text-2xl">Inclusive <span className="text-euca">Mobility</span> Group</div>
            </div>
            <p className="text-white/70 text-base leading-relaxed max-w-md">
              Australia's community and NDIS transport partner. Safe, dignified, reliable transport — delivered nationally, 24 hours a day.
            </p>
            <form onSubmit={subscribe} className="mt-8 max-w-md" data-testid="footer-newsletter">
              <label className="text-xs font-semibold uppercase tracking-[0.2em] text-white/50 mb-2 block">
                Care sector updates
              </label>
              <div className="flex gap-2">
                <input
                  type="email"
                  required
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  data-testid="footer-newsletter-input"
                  placeholder="your@email.com.au"
                  className="flex-1 rounded-full bg-white/10 border border-white/20 px-5 py-3.5 text-white placeholder:text-white/40 focus:border-brand-soft focus:bg-white/15 outline-none transition"
                />
                <button
                  type="submit"
                  disabled={busy}
                  data-testid="footer-newsletter-submit"
                  className="rounded-full bg-white text-ink px-6 py-3.5 font-semibold text-sm hover:bg-brand hover:text-white transition disabled:opacity-60"
                >
                  {busy ? '...' : 'Subscribe'}
                </button>
              </div>
            </form>
          </div>

          {/* Nav grids */}
          <div className="lg:col-span-2">
            <h4 className="text-white font-display font-bold text-sm tracking-widest uppercase mb-5">Services</h4>
            <ul className="space-y-3">
              {SERVICES.slice(0, 6).map(s => (
                <li key={s.slug}>
                  <Link to={`/services/${s.slug}`} className="text-white/60 hover:text-white text-sm transition">
                    {s.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-2">
            <h4 className="text-white font-display font-bold text-sm tracking-widest uppercase mb-5">Company</h4>
            <ul className="space-y-3">
              <li><Link to="/about" className="text-white/60 hover:text-white text-sm">About</Link></li>
              <li><Link to="/fleet" className="text-white/60 hover:text-white text-sm">Fleet</Link></li>
              <li><Link to="/service-areas" className="text-white/60 hover:text-white text-sm">Service Areas</Link></li>
              <li><Link to="/testimonials" className="text-white/60 hover:text-white text-sm">Testimonials</Link></li>
              <li><Link to="/careers" className="text-white/60 hover:text-white text-sm">Careers</Link></li>
              <li><Link to="/faq" className="text-white/60 hover:text-white text-sm">FAQ</Link></li>
              <li><Link to="/my-bookings" className="text-white/60 hover:text-white text-sm">Manage my booking</Link></li>
              <li><Link to="/refer" className="text-white/60 hover:text-white text-sm">Refer a participant</Link></li>
            </ul>
          </div>

          <div className="lg:col-span-3">
            <h4 className="text-white font-display font-bold text-sm tracking-widest uppercase mb-5">Contact</h4>
            <ul className="space-y-4">
              <li>
                <a href={`tel:${CONTACT.phone_dial}`} className="flex items-start gap-3 text-white/80 hover:text-white group">
                  <PhoneCall size={18} className="mt-0.5 text-brand-soft" />
                  <span>
                    <span className="block font-semibold" data-testid="footer-phone-display">{phoneDisplay}</span>
                    <span className="block text-xs text-white/50">{hours}</span>
                  </span>
                </a>
              </li>
              <li>
                <a href={`mailto:${contactEmail}`} className="flex items-start gap-3 text-white/80 hover:text-white">
                  <EnvelopeSimple size={18} className="mt-0.5 text-brand-soft" />
                  <span className="text-sm" data-testid="footer-email-display">{contactEmail}</span>
                </a>
              </li>
              <li className="flex items-start gap-3 text-white/60 text-sm">
                <MapPin size={18} className="mt-0.5 text-brand-soft" />
                <span>{CONTACT.hq}<br/>Serving {SERVICE_AREAS.regions.length} states &amp; territories</span>
              </li>
            </ul>
            <div className="mt-6 flex flex-col gap-2">
              <Link to="/book" className="inline-flex items-center gap-1.5 text-brand-soft font-semibold text-sm hover:text-white">
                Book online <ArrowUpRight size={16} weight="bold" />
              </Link>
              <Link to="/quote" className="inline-flex items-center gap-1.5 text-white/70 font-semibold text-sm hover:text-white">
                Request an instant quote <ArrowUpRight size={16} weight="bold" />
              </Link>
            </div>
          </div>
        </div>

        <div className="pt-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-5">
          <div className="text-white/40 text-xs">
            © {new Date().getFullYear()} Inclusive Mobility Group Pty Ltd · NDIS Registration in Progress · ABN placeholder
          </div>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-white/50">
            <Link to="/privacy" className="hover:text-white">Privacy</Link>
            <Link to="/terms" className="hover:text-white">Terms</Link>
            <Link to="/refer" className="hover:text-white">Referrals</Link>
            <Link to="/admin/login" className="hover:text-white opacity-70">Staff login</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
